"use client";

import { useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { signOut, useSession } from "next-auth/react";
import { IconVinyl, IconPinDrop, IconArtists, IconMixerSliders, IconBook } from "@/components/icons";
import { useLocale } from "@/components/locale-provider";
import { OfflineBanner } from "@/components/offline-banner";
import { OfflineSync } from "@/components/offline-sync";
import { purgeOfflineData } from "@/lib/offline/db";
import { useAvatarUrl } from "@/components/avatar-url-context";
import { hasOwnChrome } from "@/lib/own-chrome-routes";

function useNavItems() {
  const { data: session } = useSession();
  const { t } = useLocale();
  if (!session) return null;

  const home = `/${session.user.libraryOwnerUsername}`;
  const items = [
    { href: home, label: t.nav.collection, Icon: IconVinyl },
    ...(session.user.booksEnabled
      ? [{ href: `${home}/books`, label: t.nav.books, Icon: IconBook }]
      : []),
    { href: `${home}/artists`, label: t.nav.artists, Icon: IconArtists },
    { href: `${home}/browse`, label: t.nav.browse, Icon: IconPinDrop },
    { href: "/settings", label: t.nav.settings, Icon: IconMixerSliders },
  ];
  return { session, items, home };
}

function DesktopSidebar() {
  const nav = useNavItems();
  const pathname = usePathname();
  const { t } = useLocale();
  const avatarUrl = useAvatarUrl();
  if (!nav) return null;
  const { session, items } = nav;

  const ROLE_LABEL: Record<string, string> = {
    ADMIN: t.common.roleAdmin,
    VIEWER: t.common.roleUser,
  };

  return (
    <aside className="glass-bar hidden md:flex md:w-60 md:shrink-0 md:flex-col md:border-r md:border-ios-separator/50">
      <div className="flex items-center gap-2 px-5 pt-6 pb-4">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/logo.webp" alt="" className="h-7 w-7 shrink-0 rounded-[8px]" />
        <span className="text-[15px] font-semibold text-ios-label">Rakemori</span>
      </div>

      <nav className="flex-1 space-y-0.5 px-3">
        {items.map(({ href, label, Icon }) => {
          const active = pathname === href;
          return (
            <Link
              key={href}
              href={href}
              className={`flex items-center gap-3 rounded-[10px] px-3 py-2 text-[14px] transition-colors ${
                active
                  ? "bg-ios-fill font-semibold text-ios-label"
                  : "text-ios-label hover:bg-ios-fill"
              }`}
            >
              <Icon filled={active} className="h-[18px] w-[18px] shrink-0" />
              {label}
            </Link>
          );
        })}
      </nav>

      <div className="flex items-center gap-2.5 border-t border-ios-separator/50 px-4 py-3.5">
        {avatarUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ios-fill text-xs font-medium text-ios-label-secondary">
            {session.user.name?.[0]?.toUpperCase() ?? "?"}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-medium text-ios-label">{session.user.name}</p>
          <p className="truncate text-[11px] text-ios-label-secondary">
            {ROLE_LABEL[session.user.role] ?? session.user.role.toLowerCase()}
          </p>
        </div>
        <button
          onClick={async () => {
            await purgeOfflineData();
            await signOut({ callbackUrl: "/login" });
          }}
          className="press-shrink rounded-full px-2.5 py-1 text-[12px] text-ios-label-secondary hover:bg-ios-fill"
        >
          {t.common.signOut}
        </button>
      </div>
    </aside>
  );
}

function MobileTopBar() {
  const nav = useNavItems();
  const { t } = useLocale();
  const avatarUrl = useAvatarUrl();
  if (!nav) return null;
  const { session } = nav;

  return (
    <header className="glass-bar safe-top sticky top-0 z-30 flex items-center justify-between border-b border-ios-separator/50 px-4 pb-2.5 md:hidden">
      <span className="truncate text-[17px] font-semibold text-ios-label">
        {session.user.name ?? session.user.username}
      </span>
      <Link href="/settings" aria-label={t.nav.profileSettings} className="press-shrink shrink-0">
        {avatarUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={avatarUrl}
            alt=""
            className="h-8 w-8 rounded-full object-cover ring-1 ring-ios-separator/50"
          />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ios-fill text-[13px] font-medium text-ios-label-secondary">
            {session.user.name?.[0]?.toUpperCase() ?? "?"}
          </div>
        )}
      </Link>
    </header>
  );
}

/** The tab bar's footprint: its 4.25rem pill plus the inset its container pads with. */
const TAB_BAR_SPACE =
  "calc(4.25rem + max(0.75rem, var(--safe-area-inset-bottom, env(safe-area-inset-bottom))))";

function MobileTabBar() {
  const nav = useNavItems();
  const pathname = usePathname();

  // Publish the footprint so anything else docking at the bottom can clear it.
  // It cannot be hardcoded by those consumers: this bar renders only when
  // there are nav items, so a signed-out visitor browsing a public profile has
  // no tab bar at all, and a fixed offset would leave them looking at a
  // control floating 88px above nothing.
  useEffect(() => {
    const root = document.documentElement;
    if (!nav) return;
    root.style.setProperty("--tab-bar-space", TAB_BAR_SPACE);
    return () => {
      root.style.removeProperty("--tab-bar-space");
    };
  }, [nav]);

  if (!nav) return null;
  const { items } = nav;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-30 px-3 pb-[max(0.75rem,var(--safe-area-inset-bottom,env(safe-area-inset-bottom)))] md:hidden"
      // iOS Safari can lag recalculating a `position: fixed` element's layer
      // during momentum scroll — worse here since it also composites a
      // backdrop-filter blur — making it visually drift with the content
      // mid-scroll before snapping back once scrolling stops. Forcing its
      // own GPU compositing layer up front keeps it pinned throughout.
      style={{ transform: "translateZ(0)", willChange: "transform" }}
    >
      <nav className="glass-floating glass-specular mx-auto flex max-w-sm items-center justify-around gap-1 rounded-full px-2 py-1.5">
        {items.map(({ href, label, Icon }) => {
          const active = pathname === href;
          return (
            <Link key={href} href={href} className="press-shrink flex min-w-0 flex-1 justify-center py-1">
              <span
                className={`flex w-full flex-col items-center gap-0.5 rounded-2xl px-1 py-1.5 transition-colors ${
                  active ? "bg-ios-fill" : ""
                }`}
              >
                <Icon
                  filled={active}
                  className={`h-6 w-6 shrink-0 ${active ? "text-ios-accent" : "text-ios-label-secondary"}`}
                />
                <span
                  className={`w-full truncate text-center text-[10px] leading-none font-semibold ${
                    active ? "text-ios-accent-text" : "text-ios-label-secondary"
                  }`}
                >
                  {label}
                </span>
              </span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

export function AppShell({
  children,
  isConsole,
}: {
  children: React.ReactNode;
  isConsole: boolean;
}) {
  const { data: session } = useSession();
  const pathname = usePathname();

  // The operator console supplies its own chrome from src/app/admin/layout.tsx.
  // The decision comes from the server as a prop, not from usePathname: the
  // console is a *host*, and the proxy rewrites /users to /admin/users
  // server-side, so on the client usePathname reports /users — the browser
  // path, never the rewritten one. Checking for /admin here therefore held on
  // the server and failed on the client, which both wrapped the console in the
  // consumer sidebar (the exact thing this branch prevents) and mismatched the
  // hydrated tree against the server HTML. Deciding it in the root layout,
  // where the Host header is readable, makes the two renders agree.
  //
  // Note this is by host rather than by session for the original reason too: an
  // admin *does* have a session, so the branch below would otherwise wrap the
  // console in library and books nav that means nothing there.
  if (isConsole) return <>{children}</>;

  // The landing page, the legal pages and the install guide render their own
  // header and footer, so a signed-in visitor was given two navigations at
  // once: the library sidebar pinned beside a page that already offers a
  // Dashboard button. They get the same bare frame a signed-out visitor gets.
  //
  // usePathname rather than a prop from the root layout, unlike `isConsole`
  // above: the root layout is shared by every route and is NOT re-rendered on
  // a client-side navigation, so a value derived there would still say
  // "landing page" after the visitor clicked through to their library. The
  // hydration trap that forced `isConsole` onto the server does not apply
  // here, because none of these paths is rewritten — the browser's path and
  // the rendered route are the same string.
  if (!session || hasOwnChrome(pathname)) {
    return (
      <div className="min-h-screen bg-ios-bg">
        <OfflineBanner />
        {/* The signed-in branch below wraps its children in <main>; this one
            needs its own, or the whole chrome-less surface — landing page,
            login, legal pages — ships with no main landmark to skip to.

            It carries the preview-bar reserve as well. This branch is what a
            signed-out visitor browsing a public profile gets, which is the
            whole audience for track previews, and the mini-player is
            `position: fixed` — without this the last row of a tracklist sits
            behind it with no way to scroll clear. Resolves to 0px on every
            page where nothing is playing. */}
        <main className="pb-[var(--preview-bar-space,0px)]">{children}</main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen md:h-screen md:overflow-hidden">
      <OfflineBanner />
      <OfflineSync />
      <DesktopSidebar />
      <div className="flex min-h-screen min-w-0 flex-1 flex-col md:h-screen md:overflow-y-auto">
        <MobileTopBar />
        {/* The floating tab bar is `position: fixed`, so this reserve is the only
            thing keeping the last row of content scrollable clear of it. Its
            height is not constant: the bar is 4.25rem of nav plus the same
            `max(0.75rem, safe-area-inset-bottom)` its container pads with, so a
            flat reserve silently under-shoots on devices with a bottom inset
            (gesture bars, home indicators) and clips the final rows. Tracking
            the inset here keeps the gap at a constant 1rem, and resolves to the
            previous 6rem wherever the inset is 0. */}
        {/* `--preview-bar-space` is set by AudioPreviewProvider while the
            preview mini-player is on screen and removed when it is dismissed.
            The bar is `position: fixed` and so reserves no room of its own;
            without this the last tracklist row scrolls under it. Resolves to
            0px whenever nothing is playing, leaving the padding as it was. */}
        <main className="momentum-scroll min-w-0 flex-1 pb-[calc(5.25rem+max(0.75rem,var(--safe-area-inset-bottom,env(safe-area-inset-bottom)))+var(--preview-bar-space,0px))] md:pb-[var(--preview-bar-space,0px)]">
          {children}
        </main>
      </div>
      <MobileTabBar />
    </div>
  );
}
